import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  groupFulfillmentOrderLineItems,
  ungroupFulfillmentOrderLineItems,
} from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import {
  ModalItemsTable,
  splitGroupForm,
  splitGroupFormDefault,
} from "./modal-items-table";

export const SplitGroupModal = memo(function SplitGroupModal({
  group,
  open,
  setOpen,
  setRows,
  triggerReload,
  isSlideout,
}: {
  group: FulfillmentGroupData;
  open: boolean;
  setOpen: (val: boolean) => void;
  setRows?: (value: React.SetStateAction<FulfillmentGroupData[]>) => void;
  triggerReload: () => Promise<void>;
  isSlideout: boolean;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);
  const team = useContext(TeamContext);
  const { ordersOutboundLabelsHref } = useRequiredContext(
    MerchantRoutesContext,
  );
  const params = useParams<{ orderId: string }>();
  const { orderId: orderIdFromParams } = params;
  const [searchParams] = useSearchParams();
  const isRedoOrder = searchParams.get("isRedoOrder");
  const orderId = isRedoOrder === "false" ? undefined : orderIdFromParams;

  const items = useMemo(() => {
    return groupFulfillmentOrderLineItems(group.items, {
      usePrice: true,
      degroupByCustomAttributes:
        team?.settings.outboundLabels?.degroupItemsByCustomAttributes,
    });
  }, [group, team?.settings.outboundLabels?.degroupItemsByCustomAttributes]);

  const form = useInput(splitGroupForm, splitGroupFormDefault);

  useEffect(() => {
    form.setValue({ data: Array(items.length).fill(0) });
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const [splitLoad, triggerSplitLoad] = useTriggerLoad(async () => {
    const newItems = form.value.data.map((quantity, index) => {
      const item = items[index];
      return { ...item, quantity };
    });

    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const ungrouped = ungroupFulfillmentOrderLineItems(group.items, newItems);

    const newGroupsThatNeedLinking: { groupId: string; redoOrderId: string }[] =
      [];
    try {
      const {
        success,
        warningMessage,
        newGroupIds,
        newGroupsByRedoOrderId,
        pickSessionDisposition,
      } = await client.splitFulfillmentGroup({
        originalGroupId: group._id,
        itemsToSplit: ungrouped.map((item) => ({
          fulfillmentOrderLineItemId: item.externalId,
          quantity: item.quantity,
        })),
      });

      for (const [redoOrderId, groupIds] of Object.entries(
        newGroupsByRedoOrderId || {},
      )) {
        if (redoOrderId !== orderId) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          newGroupsThatNeedLinking.push({ groupId: groupIds[0], redoOrderId });
        }
      }

      if (!success && warningMessage) {
        toast(warningMessage, { variant: "warning" });
      } else if (newGroupIds && setRows) {
        const response = await client.getReplacementGroups({
          groupIds: newGroupIds,
        });
        const newGroups = response.replacements.map(
          (replacement) => replacement.replacementGroup,
        );

        setRows((currentRows: FulfillmentGroupData[]) => {
          const groupIndex = currentRows.findIndex(
            (row) => row._id === group._id,
          );
          if (groupIndex !== -1) {
            const newRows = [...currentRows];
            newRows.splice(groupIndex + 1, 0, ...newGroups);
            return newRows;
          }
          return currentRows;
        });
      }
      if (success && pickSessionDisposition === "reconciled") {
        toast(
          "Printed pick session updated. Verify and finish each shipment separately.",
          { variant: "success" },
        );
      } else if (success && pickSessionDisposition === "cancelled") {
        toast(
          "The pick session was cancelled because its reservations could not be safely split. Create and print a new pick session.",
          { variant: "warning", persist: true },
        );
      }
    } catch (err) {
      toast("Failed to split shipment", { variant: "error" });
    } finally {
      if (newGroupsThatNeedLinking.length > 0 && !isSlideout) {
        let newGroupIndex = 0;
        for (const { groupId, redoOrderId } of newGroupsThatNeedLinking) {
          newGroupIndex++;
          toast(
            <Flex align="center" justify="space-between" w="full">
              <Text>
                New shipment {newGroupIndex} is not shown on this page.
              </Text>
              <RedoButton
                hierarchy="primary"
                IconTrailing={ExternalLinkIcon}
                onClick={() => {
                  window.open(
                    `${ordersOutboundLabelsHref}/orders/${redoOrderId}?outboundOrderTab=${groupId}&isRedoOrder=true`,
                    "_blank",
                  );
                }}
                size="xs"
                text="Go to new shipment"
              />
            </Flex>,
            { variant: "success", persist: true },
          );
        }
      }
      await triggerReload();
      setOpen(false);
    }
  });

  const totalQuantity = form.inputs.data.inputs.reduce(
    (acc, input) => acc + input.value,
    0,
  );

  return (
    <RedoBaseModal
      isOpen={open}
      onModalCloseRequested={() => {
        setOpen(false);
      }}
    >
      <RedoModalHeader
        cancelClicked={() => setOpen(false)}
        subtitle="Create multiple shipments and move items to this new shipment."
        title="Create split shipment"
      />
      <ModalItemsTable form={form} items={items} />
      <Flex
        align="center"
        dir="column"
        grow={1}
        justify="center"
        p="3xl"
        w="full"
      >
        <Flex dir="row" gap="md" justify="flex-end" w="full">
          <RedoButton
            hierarchy="secondary"
            onClick={() => setOpen(false)}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            disabled={
              totalQuantity === 0 || totalQuantity === group.totalItemQuantity
            }
            hierarchy="primary"
            onClick={() => {
              triggerSplitLoad();
            }}
            pending={splitLoad.pending}
            size="md"
            text="Confirm"
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
